/**
 * Design system tokens — single source of truth for colors used across the app.
 * Import named tokens instead of using raw hex values inline.
 */

// ─── Surface / Background ────────────────────────────────────────────────────
export const colorBackground = "#F3F4F6";
export const colorSurface = "#FFFFFF";
export const colorSurfaceHover = "#F9FAFB";
export const colorSurfaceDisabled = "#f3f3f5"
export const colorSurfaceError = "#fff5f5";
export const colorSurfaceSuccess = "#f0fdf4"
export const colorBackgroundInfo = "#eff6ff"

// ─── Text ─────────────────────────────────────────────────────────────────────
export const colorTextPrimary = "#111827";
export const colorTextSecondary = "#374151";
export const colorTextMuted = "#6B7280";
export const colorTextDisabled = "#9CA3AF";
export const colorTextSuccess = "#008236"
export const colorTextError = "#d32f2f"

// ─── Border ───────────────────────────────────────────────────────────────────
export const colorBorder = "#E5E7EB";
export const colorBorderHover = "#D1D5DB";
export const colorBorderFocus = "#6B7280";
export const colorBorderSuccess = "#b9f8cf"
export const colorBorderLight = "#0000001a"
export const colorBorderInfo = '#bedbff'
export const colorBorderError = "#fecaca";

// ─── Brand / Accent ───────────────────────────────────────────────────────────
export const colorAccent = "#1abc9c";
export const colorAccentHover = "#17a589";

// ─── Action (primary CTA) ─────────────────────────────────────────────────────
export const colorActionPrimary = "#111827";
export const colorActionPrimaryHover = "#374151";
export const colorActionPrimaryText = "#FFFFFF";

// ─── Status ───────────────────────────────────────────────────────────────────
export const colorSuccess = "#10B981";
export const colorError = "#EF4444";
export const colorWarning = "#F59E0B";

// ─── Chip / Tag ───────────────────────────────────────────────────────────────
export const colorChipBackground = "#F3F4F6";
export const colorChipText = "#374151";
export const colorChipBorder = "#D1D5DB";

// ─── Shadow ───────────────────────────────────────────────────────────────────
export const shadowCard = "0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.04)";
export const shadowCardHover = "0px 4px 6px rgba(0,0,0,0.07), 0px 2px 4px rgba(0,0,0,0.06)";

// ─── Radius ───────────────────────────────────────────────────────────────────
export const radiusOuter = "24px";
export const radiusInner = "16px";
export const radiusCard = "12px";
export const radiusInput = "8px";
export const radiusChip = "6px";
export const radiusButton = "8px";

// ─── Deal / Offer (amber) ─────────────────────────────────────────────────────
export const colorDealBg = "#FEF3C7";
export const colorDealBorder = "#FDE68A";
export const colorDealText = "#92400E";
export const colorDealAccent = "#D97706";

// ─── Extended status ──────────────────────────────────────────────────────────
export const colorBorderSuccessEmphasis = "#6EE7B7";
export const colorActionDanger = "#DC2626";
export const colorSurfaceDangerHover = "#FEE2E2";
export const colorTextInfo = "#1D4ED8";
export const colorSurfaceWarning = "#FFFBEB";
export const colorBorderWarning = "#FDE68A";
export const colorTextWarning = "#92400E";
export const colorActionWarning = "#B45309";

// ─── Icon Outline ─────────────────────────────────────────────────────────────
export const iconColorSuccess = '#059669'
export const iconColorInfo = '#2563EB'
export const iconColorPurple = '#7C3AED'
export const iconColorPink = '#DB2777'

// ─── Icon Background ──────────────────────────────────────────────────────────
export const iconBgSuccess = '#D1FAE5'
export const iconBgInfo = '#DBEAFE'
export const iconBgPurple = '#EDE9FE'
export const iconBgPink = '#FCE7F3'

// ─── Table schedule (booking slot states) ────────────────────────────────────
// Emerald scale used by the table-schedule board: a confirmed booking fills a
// slot with the dark emerald, a pending (awaiting confirmation) booking with the
// light emerald, and free slots stay neutral.
export const colorSlotConfirmed = "#10B981";
export const colorSlotConfirmedHover = "#059669";
export const colorSlotConfirmedText = "#FFFFFF";
export const colorSlotPending = "#A7F3D0";
export const colorSlotPendingHover = "#6EE7B7";
export const colorSlotPendingText = "#065F46";
export const colorSlotEmpty = "#F3F4F6";
export const colorSlotEmptyHover = "#E5E7EB";

// Empty slots that fall inside an active deal/event window are tinted by type:
// gold (amber) for deals, purple for events. Reuses the deal and purple-icon
// hues so the board stays consistent with the rest of the app.
export const colorSlotDeal = "#FEF3C7";
export const colorSlotDealHover = "#FDE68A";
export const colorSlotDealText = "#92400E";
export const colorSlotEvent = "#EDE9FE";
export const colorSlotEventHover = "#DDD6FE";
export const colorSlotEventText = "#5B21B6";

// ─── Heatmap (offer performance covers) ──────────────────────────────────────
// RGB channels of the heatmap fill (matches iconColorInfo). Alpha is applied
// per cell, scaled to the cover count, so busier weekdays read as deeper blue.
export const colorHeatmapBaseRgb = "37, 99, 235";

// ─── Landing Page ─────────────────────────────────────────────────────────────
export const colorLandingBackground = "#F3FAF7";
export const colorCTAOrange = "#1abc9c";
export const colorCTAOrangeHover = "#17a589";
export const colorCTAOrangeLight = "rgba(26, 188, 156, 0.12)";
export const colorSaasBadgeBg = "#E6F7F3";
export const colorSaasBadgeText = "#0e7a67";
export const colorLandingFooterBg = "#0d2b22";

// ─── Public menu (client-facing dark page) ───────────────────────────────────
// Dark, embellished menu page shown to anonymous diners. Warm near-black
// background with cream serif text and a gold accent for prices / active nav.
export const colorPublicMenuBg = "#0F0D0C";
export const colorPublicMenuSurface = "#161311";
export const colorPublicMenuText = "#ECE4D6";
export const colorPublicMenuTextMuted = "#9A9186";
export const colorPublicMenuAccent = "#C9A24B";
export const colorPublicMenuDivider = "rgba(236, 228, 214, 0.10)";
export const colorPublicMenuChipBorder = "rgba(201, 162, 75, 0.45)";
export const colorPublicMenuChipText = "#D9C7A0";
// Scrim laid over the cover hero for text legibility; the hero fades into the
// page background at its lower edge.
export const colorPublicMenuHeroScrim = "rgba(15, 13, 12, 0.35)";
// ─── Landing page (refonte) ──────────────────────────────────────────────────
// Palette of the reworked public landing page. It is deliberately separate from
// the in-app palette above: the landing runs a warmer emerald brand, a near-black
// display ink and a slate hero, and must not drift when the dashboard theme moves.
export const colorLandingBrand = "#33bd88";
export const colorLandingBrandStrong = "#20a875";
export const colorLandingBrandSoft = "#e4f7ef";
export const colorLandingBrandLine = "#d3efe1";
export const colorLandingBrandBar = "#54cd9a";
export const colorLandingInk = "#0f1216";
export const colorLandingText = "#1b2029";
export const colorLandingMuted = "#6b7280";
export const colorLandingLine = "#e8ebef";
export const colorLandingSurface = "#ffffff";
export const colorLandingTint = "#f2fbf7";
export const colorLandingHeroBg = "#2c3e50";
export const colorLandingHeroText = "#c7d1da";
export const colorLandingBlue = "#4f7cf6";
export const colorLandingBlueSoft = "#e9effe";

// RGB channels of colorLandingBlue, used as the per-cell heatmap fill in the
// landing page's offer/event performance tables. Alpha scales with covers.
export const colorLandingHeatmapBaseRgb = "79, 124, 246";

// Accent hues for the offer / event duality shown across the mockups and the
// offer-performance table: amber marks an offer, violet marks an event.
export const colorLandingOffer = "#f5a623";
export const colorLandingOfferSoft = "#fef7e0";
export const colorLandingEvent = "#8b5cf6";
export const colorLandingEventSoft = "#efe9ff";
export const colorLandingEventText = "#7c3aed";
export const colorLandingWhatsapp = "#128a3e";
export const colorLandingWhatsappSoft = "#e6f8ee";

// Dark surfaces: the "créneaux creux" panel inside the dashboard mock and the
// landing footer both sit on the display ink.
export const colorLandingDarkPanel = "#191d24";
export const colorLandingDarkBar = "#2a303a";
export const colorLandingDarkText = "#dfe4ea";
export const colorLandingDarkMuted = "#8b93a1";
export const colorLandingFooterText = "#9aa3b0";
export const colorLandingFooterLink = "#cfd5dd";
export const colorLandingFooterBorder = "#262b32";

export const shadowLandingFlat = "0 1px 2px rgba(16,24,40,.05)";
export const shadowLandingRaised =
  "0 2px 6px rgba(16,24,40,.06),0 16px 40px rgba(16,24,40,.07)";
export const shadowLandingBrand = "0 10px 26px rgba(51,189,136,.32)";

export const radiusLandingSection = "26px";
export const radiusLandingPanel = "20px";
export const radiusLandingCard = "18px";
export const radiusLandingControl = "12px";

// Display / body faces used by the landing page (loaded in public/index.html).
export const fontLandingDisplay = '"Bevan", Georgia, "Times New Roman", serif';
export const fontLandingSans = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
export const fontLandingScript = '"Pacifico", "Bevan", cursive';

// Chrome of the browser-framed booking-page mock, plus the two on-dark shades
// used by the app-store buttons and the cream wordmark on the ink header.
export const colorLandingBrowserChrome = "#eef1f4";
export const colorLandingBrowserDotRed = "#ff5f57";
export const colorLandingBrowserDotYellow = "#febc2e";
export const colorLandingBrowserDotGreen = "#28c840";
export const colorLandingCream = "#efe7d3";
export const colorLandingOnDarkMuted = "#aab2bf";
export const colorLandingInkPressed = "#000000";
export const colorLandingBarInactive = "#dfe6ea";

// RGB channels of colorLandingBrand, for the scrim that fades the demo-section
// photo into the brand gradient; and the deep green the on-gradient headings
// are shadowed with.
export const colorLandingBrandRgb = "51, 189, 136";
export const colorLandingOnBrandShadowRgb = "6, 59, 44";
